(() => {
  // =====================================================
  // PDP FULLSCREEN ZOOM — STRICT PDP SCOPED + DESKTOP SAFE + PAN + WHEEL PAN
  // =====================================================

  // ----- Hard reset on reinit -----
  if (window.__PDP_ZOOM_HANDLER) {
    document.removeEventListener("click", window.__PDP_ZOOM_HANDLER, true);
    document.removeEventListener("pointerup", window.__PDP_ZOOM_HANDLER, true);
    document.removeEventListener("touchend", window.__PDP_ZOOM_HANDLER, true);
    delete window.__PDP_ZOOM_HANDLER;
  }
  if (window.__PDP_ZOOM_CLICK_KILLER) {
    document.removeEventListener("click", window.__PDP_ZOOM_CLICK_KILLER, true);
    delete window.__PDP_ZOOM_CLICK_KILLER;
  }

  document.querySelector(".pdp-zoom-overlay")?.remove();
  window.__PDP_ZOOM_FINAL_INIT = false;

  const IMG_SEL = 'img[data-aid="image"]';

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const toWidth = (url, w) => {
    if (!url) return url;
    if (url.includes("width=")) return url.replace(/width=\d+/g, `width=${w}`);
    const join = url.includes("?") ? "&" : "?";
    return `${url}${join}width=${w}`;
  };

  const bestSrc = (img) => img?.currentSrc || img?.src || "";

  // NEVER intercept these
  const IGNORE_CONTAINERS = [
    ".product-recommendations",
    ".recommendations",
    ".related-products",
    ".you-may-also-like",
    '[data-aid*="recommend"]',
    '[data-aid*="related"]',
  ].join(",");

  // -----------------------------------------------------
  // Get strict PDP root (based on cart form)
  // -----------------------------------------------------
  function getPdpRoot() {
    const cartForm = document.querySelector('main form[action*="/cart/add"]');
    if (!cartForm) return null;
    return cartForm.closest("section") || cartForm.closest("main");
  }

  // -----------------------------------------------------
  // Collect images STRICTLY inside product media container
  // -----------------------------------------------------
  function collectGalleryImages(clickedImg) {
    const galleryRoot =
      clickedImg.closest('[data-armada-selector="product-media-container"]') ||
      clickedImg.closest('[data-armada-selector="product-media"]') ||
      clickedImg.closest("armada-modal-open") ||
      getPdpRoot();

    if (!galleryRoot) return [];

    const imgs = [
      ...galleryRoot.querySelectorAll(
        'img[data-aid="image"][data-media-id], [data-armada-selector="product-media"] img[data-aid="image"], armada-modal-open img[data-aid="image"]'
      ),
    ];

    const found = [];
    const seen = new Set();

    imgs.forEach((img) => {
      const raw = bestSrc(img);
      if (!raw) return;

      const base = String(raw).split("?")[0];
      if (!base || seen.has(base)) return;
      seen.add(base);

      found.push({
        mediaId: img.getAttribute("data-media-id") || img.dataset.mediaId || "",
        src: base,
        thumb: toWidth(base, 160),
      });
    });

    return found;
  }

  // -----------------------------------------------------
  // Overlay
  // -----------------------------------------------------
  function ensureOverlay() {
    let overlay = document.querySelector(".pdp-zoom-overlay");
    if (overlay) return overlay;

    overlay = document.createElement("div");
    overlay.className = "pdp-zoom-overlay";
    overlay.innerHTML = `
      <div class="pdp-zoom-stage"></div>
      <div class="pdp-zoom-rail pdp-zoom-rail-left"></div>
      <div class="pdp-zoom-rail pdp-zoom-rail-right"></div>
      <div class="pdp-zoom-controls">
        <button class="pdp-zoom-minus" type="button">−</button>
        <button class="pdp-zoom-plus" type="button">+</button>
      </div>
      <button class="pdp-zoom-close" type="button">×</button>
    `;
    document.body.appendChild(overlay);
    return overlay;
  }

  // Used to kill the theme’s click-modal right after we open ours (DESKTOP FIX)
  let __lastZoomOpenAt = 0;
  const CLICK_KILL_WINDOW_MS = 700;

  function installClickKiller() {
    if (window.__PDP_ZOOM_CLICK_KILLER) return;

    window.__PDP_ZOOM_CLICK_KILLER = (e) => {
      if (!__lastZoomOpenAt) return;
      const dt = Date.now() - __lastZoomOpenAt;
      if (dt > CLICK_KILL_WINDOW_MS) return;

      const img = e.target.closest(IMG_SEL);
      if (!img) return;

      const mediaContainer =
        img.closest('[data-armada-selector="product-media-container"]') ||
        img.closest('[data-armada-selector="product-media"]') ||
        img.closest("armada-modal-open");

      if (!mediaContainer) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    document.addEventListener("click", window.__PDP_ZOOM_CLICK_KILLER, true);
  }

  function openZoomFrom(imgEl) {
    const overlay = ensureOverlay();
    const stage = overlay.querySelector(".pdp-zoom-stage");
    const railLeft = overlay.querySelector(".pdp-zoom-rail-left");
    const railRight = overlay.querySelector(".pdp-zoom-rail-right");

    const btnPlus = overlay.querySelector(".pdp-zoom-plus");
    const btnMinus = overlay.querySelector(".pdp-zoom-minus");
    const btnClose = overlay.querySelector(".pdp-zoom-close");

    stage.innerHTML = "";
    railLeft.innerHTML = "";
    railRight.innerHTML = "";

    const zoomImg = document.createElement("img");
    zoomImg.className = "pdp-zoom-img";
    stage.appendChild(zoomImg);

    // Make stage the interaction surface
    zoomImg.style.pointerEvents = "none";
    stage.style.touchAction = "none";
    stage.style.userSelect = "none";
    stage.style.cursor = "grab";

    document.body.classList.add("pdp-zoom-lock");
    overlay.classList.add("is-open");

    __lastZoomOpenAt = Date.now();
    installClickKiller();

    // ================================
    // START SCALE CONFIG
    // ================================
    const isMobile = window.matchMedia("(max-width: 749px)").matches;
    const MOBILE_START_SCALE_MULTIPLIER = 7;

    // desktop: start ~2x smaller than your earlier aggressive open
    const DESKTOP_START_SCALE_MULTIPLIER = 1.15; // ~1.05–1.35
    const DESKTOP_MIN_START_SCALE = 1.05;        // hard floor

    // Pan state
    const pan = {
      tx: 0,
      ty: 0,
      active: false,
      pid: null,
      startX: 0,
      startY: 0,
      startTx: 0,
      startTy: 0,
    };

    const state = {
      scale: 1,
      minScale: 1,
      maxScale: isMobile ? 8 : 4,
    };

    const getBounds = () => {
      const rect = stage.getBoundingClientRect();
      const vw = rect.width || window.innerWidth;
      const vh = rect.height || window.innerHeight;

      const nw = zoomImg.naturalWidth || 1;
      const nh = zoomImg.naturalHeight || 1;

      const scaledW = nw * state.scale;
      const scaledH = nh * state.scale;

      const maxX = Math.max(0, (scaledW - vw) / 2);
      const maxY = Math.max(0, (scaledH - vh) / 2);

      return { maxX, maxY };
    };

    const apply = () => {
      const { maxX, maxY } = getBounds();
      pan.tx = clamp(pan.tx, -maxX, maxX);
      pan.ty = clamp(pan.ty, -maxY, maxY);

      zoomImg.style.transform =
        `translate(calc(-50% + ${pan.tx}px), calc(-50% + ${pan.ty}px)) scale(${state.scale})`;
    };

    const fit = () => {
      const vw = window.innerWidth * 0.9;
      const vh = window.innerHeight * 0.9;
      const nw = zoomImg.naturalWidth || 1;
      const nh = zoomImg.naturalHeight || 1;

      state.minScale = Math.min(vw / nw, vh / nh, 1);

      const startScale = isMobile
        ? clamp(
            state.minScale * MOBILE_START_SCALE_MULTIPLIER,
            state.minScale,
            state.maxScale
          )
        : clamp(
            Math.max(DESKTOP_MIN_START_SCALE, state.minScale * DESKTOP_START_SCALE_MULTIPLIER),
            state.minScale,
            state.maxScale
          );

      state.scale = startScale;
      pan.tx = 0;
      pan.ty = 0;
      apply();
    };

    const setImage = (src, idx) => {
      zoomImg.src = toWidth(src, 2400) || src;
      if (zoomImg.complete) fit();
      else zoomImg.onload = fit;

      [...railLeft.children].forEach((b, i) => b.classList.toggle("is-active", i === idx));
      [...railRight.children].forEach((b, i) => b.classList.toggle("is-active", i === idx));
    };

    const images = collectGalleryImages(imgEl);

    images.forEach((item, idx) => {
      const t = document.createElement("button");
      t.className = "pdp-zoom-thumb";
      t.type = "button";
      t.innerHTML = `<img src="${item.thumb}" alt="">`;
      t.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setImage(item.src, idx);
      };
      railLeft.appendChild(t);

      const n = document.createElement("button");
      n.className = "pdp-zoom-num";
      n.type = "button";
      n.textContent = idx + 1;
      n.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setImage(item.src, idx);
      };
      railRight.appendChild(n);
    });

    const baseHero = (bestSrc(imgEl) || "").split("?")[0];
    const heroIndex = Math.max(0, images.findIndex((x) => x.src === baseHero));
    setImage(baseHero || images[0]?.src, heroIndex);

    btnPlus.onclick = (e) => {
      e.preventDefault();
      state.scale = clamp(state.scale * 1.25, state.minScale, state.maxScale);
      apply();
    };

    btnMinus.onclick = (e) => {
      e.preventDefault();
      state.scale = clamp(state.scale / 1.25, state.minScale, state.maxScale);
      apply();
    };

    const close = () => {
      overlay.classList.remove("is-open");
      document.body.classList.remove("pdp-zoom-lock");
      setTimeout(() => { __lastZoomOpenAt = 0; }, 50);
    };

    btnClose.onclick = close;

    // ----------------
    // PAN (drag to move)
    // ----------------
    const isUI = (el) =>
      !!el.closest(".pdp-zoom-controls, .pdp-zoom-close, .pdp-zoom-rail, button, a");

    const onDown = (e) => {
      if (!overlay.classList.contains("is-open")) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (isUI(e.target)) return;
      if (state.scale <= state.minScale + 0.001) return;

      pan.active = true;
      pan.pid = e.pointerId;
      pan.startX = e.clientX;
      pan.startY = e.clientY;
      pan.startTx = pan.tx;
      pan.startTy = pan.ty;

      stage.setPointerCapture?.(e.pointerId);
      stage.style.cursor = "grabbing";
      e.preventDefault();
    };

    const onMove = (e) => {
      if (!pan.active) return;
      if (pan.pid != null && e.pointerId !== pan.pid) return;

      pan.tx = pan.startTx + (e.clientX - pan.startX);
      pan.ty = pan.startTy + (e.clientY - pan.startY);
      apply();
      e.preventDefault();
    };

    const onUp = (e) => {
      if (!pan.active) return;
      if (pan.pid != null && e.pointerId !== pan.pid) return;

      pan.active = false;
      pan.pid = null;
      stage.releasePointerCapture?.(e.pointerId);
      stage.style.cursor = "grab";
    };

    stage.addEventListener("pointerdown", onDown, { passive: false, capture: true });
    stage.addEventListener("pointermove", onMove, { passive: false, capture: true });
    stage.addEventListener("pointerup", onUp, { capture: true });
    stage.addEventListener("pointercancel", onUp, { capture: true });

    // ----------------
    // WHEEL / TRACKPAD SCROLL PAN (+ pinch zoom)
    // ----------------
    const onWheel = (e) => {
      if (!overlay.classList.contains("is-open")) return;
      if (isUI(e.target)) return;

      // Trackpad pinch zoom (ctrlKey true on many browsers)
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const zoomFactor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
        state.scale = clamp(state.scale * zoomFactor, state.minScale, state.maxScale);
        apply();
        return;
      }

      // If not zoomed in, let wheel do nothing special (but still prevent page scroll while overlay open)
      if (state.scale <= state.minScale + 0.001) {
        e.preventDefault();
        return;
      }

      // Normal wheel pans image
      // deltaX handles 2-finger horizontal; deltaY vertical.
      // Holding shift typically means horizontal scroll; support that too.
      const dx = e.shiftKey ? e.deltaY : e.deltaX;
      const dy = e.shiftKey ? 0 : e.deltaY;

      // Invert so "scroll down" moves image down naturally
      pan.tx -= dx;
      pan.ty -= dy;

      apply();
      e.preventDefault();
    };

    stage.addEventListener("wheel", onWheel, { passive: false, capture: true });

    // Quick sanity marker for you
    overlay.__PDP_ZOOM_STATE__ = { state, pan, apply };
  }

  // -----------------------------------------------------
  // MAIN HANDLER (desktop needs click, mobile can use pointerup)
  // -----------------------------------------------------
  function shouldIgnoreTap(e, mediaContainer) {
    const hardIgnore = e.target.closest(
      'form[action*="/cart/add"], fieldset, legend, label, input, select, [data-aid*="option"], [data-aid*="variant"], [data-name*="option"], [data-name*="variant"], [data-armada-selector*="product-form"], [data-armada-selector*="quantity"], [data-aid="armament-radio-item-size"], color-swatch, size-swatch'
    );
    if (hardIgnore) return true;

    const actionWrap = e.target.closest('button, a, [role="button"]');
    if (actionWrap && mediaContainer && mediaContainer.contains(actionWrap)) {
      return false;
    }

    const generalControl = e.target.closest("button, a, [role='button']");
    return !!generalControl;
  }

  window.__PDP_ZOOM_HANDLER = (e) => {
    if (e.type === "pointerup" && e.button != null && e.button !== 0) return;
    if (e.type === "click" && e.button != null && e.button !== 0) return;

    const img = e.target.closest(IMG_SEL);
    if (!img) return;

    if (img.closest(IGNORE_CONTAINERS)) return;

    const pdpRoot = getPdpRoot();
    if (!pdpRoot || !pdpRoot.contains(img)) return;

    const mediaContainer =
      img.closest('[data-armada-selector="product-media-container"]') ||
      img.closest('[data-armada-selector="product-media"]') ||
      img.closest("armada-modal-open");

    if (!mediaContainer) return;

    if (shouldIgnoreTap(e, mediaContainer)) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    openZoomFrom(img);
  };

  document.addEventListener("pointerup", window.__PDP_ZOOM_HANDLER, true);
  document.addEventListener("click", window.__PDP_ZOOM_HANDLER, true);

  window.__PDP_ZOOM_FINAL_INIT = true;
  console.log("[PDP ZOOM] init ✅ (STRICT + CLICK SAFE + PAN + WHEEL PAN)");
})();

